import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import NewsletterInput from "./NewsletterInput";

const breakpoints = [390, 755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const footerSections = [
  {
    title: "Shop",
    links: [
      { title: "Shop All", url: "/shop" },
      { title: "Edibles", url: "/product-category/edibles" },
      { title: "Flower", url: "/product-category/flower" },
      { title: "Popular Products", url: "/product-category/popular" },
      { title: "Beverages", url: "/product-category/beverages" },
      { title: "Prerolls", url: "/product-category/prerolls" },
      { title: "Staff Picks", url: "/product-category/staff-picks" },
      { title: "Enhancers", url: "/product-category/enhancers" },
      { title: "Vape", url: "/product-category/vape" },
    ],
  },
  {
    title: "Knowledge",
    links: [
      { title: "Legality", url: "/knowledge/fullproof-legal" },
      { title: "Cannabinoids", url: "/knowledge/what-are-cannabinoids" },
      { title: "Kava", url: "/knowledge/kava" },
      { title: "Mushroom", url: "/knowledge/mushrooms" },
      { title: "Quality Control", url: "/knowledge/high-quality" },
      { title: "About Us", url: "/about-us" },
    ],
  },
  {
    title: "Help",
    links: [
      { title: "Help Center", url: "/help-center" },
      { title: "Support", url: "/support" },
      { title: "Shipping", url: "/help-center" },
      { title: "Refund Policy", url: "/help-center" },
    ],
  },
  {
    title: "Legal",
    links: [
      { title: "Terms of Service", url: "/legal/terms-of-service" },
      { title: "Privacy Policy", url: "/legal/privacy-policy" },
      { title: "Return Policy", url: "/legal/terms-of-service#section-10" },
    ],
  },
];

const Footer = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [signupComplete, setSignupComplete] = useState(false);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <footer css={styles.footerContainer}>
      <div css={{ maxWidth: "1120px", margin: "0 auto" }}>
        {/* Newsletter Signup */}
        <div css={styles.newsletterContainer}>
          <h2 css={styles.newsletterTitle}>
            Join our community. Get 20% off your first order.
          </h2>
          <div css={styles.inputContainer}>
            <NewsletterInput onSignupComplete={() => setSignupComplete(true)} type={"footer"} />
          </div>
        </div>


        {/* Footer Links */}
        {footerSections.map((section, index) => (
          <div key={index} css={styles.footerSection}>
            <button css={styles.sectionTitle} onClick={() => handleToggle(index)}>
              <span>{section.title}</span>
              {openIndex === index ? <ExpandLessIcon css={styles.icon} /> : <ExpandMoreIcon css={styles.icon} />}
            </button>
            {openIndex === index && (
              <div css={styles.linkContainer}>
                <div css={styles.linkGrid}>
                  {section.links.map((link, linkIndex) => (
                    <Link key={linkIndex} css={styles.linkText} to={link.url}>
                      {link?.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Accepted payment methods */}
        <div css={styles.paymentMethods}>
          <span css={styles.paymentMethodsLabel}>We accept</span>
          <ul css={styles.paymentMethodsList}>
            {[
              { src: "/images/payment/amex.svg", alt: "American Express" },
              { src: "/images/payment/mastercard.svg", alt: "Mastercard" },
              { src: "/images/payment/visa.webp", alt: "Visa", needsCardBg: true },
              { src: "/images/payment/discover.png", alt: "Discover" },
            ].map((card) => (
              <li
                key={card.alt}
                css={[styles.paymentMethodItem, card.needsCardBg && styles.paymentMethodItemCarded]}
              >
                <img src={card.src} alt={card.alt} css={styles.paymentMethodImage} />
              </li>
            ))}
          </ul>
        </div>

        {/* COA — surface-level link, intentionally outside the Legal accordion */}
        <Link to="/legal/certificates-of-analysis" css={styles.coaLink}>
          Certificates of Analysis (COA)
        </Link>

        {/* Compliance disclaimers — surface-level by design (always visible, no expand/collapse) */}
        <div css={styles.disclaimersContainer}>
          <div css={styles.disclaimerBlock}>
            <h3 css={styles.disclaimerTitle}>FDA Disclaimer</h3>
            <p css={styles.disclaimerBody}>
              The products available have not been evaluated by the Food and Drug Administration (FDA). These products are not intended to diagnose, treat, cure, or prevent any disease, and their effectiveness has not been confirmed by FDA-approved research. Always consult a qualified healthcare provider before using any product, especially if you are pregnant, nursing, taking medications, or have a medical condition. All products sold are derived from industrial hemp and comply with the 2018 Farm Bill, containing less than 0.3% Delta-9 THC by dry weight. While federally legal, hemp laws vary by state and locality. It is the sole responsibility of the customer to understand and follow all applicable laws before making a purchase. We do not offer medical advice and assume no responsibility for any legal consequences resulting from the use or possession of our products in jurisdictions where they may be restricted. By accessing and using this website, you agree to our{" "}
              <Link css={styles.disclaimerLink} to="/legal/privacy-policy">Privacy Policy</Link>
              {" "}and{" "}
              <Link css={styles.disclaimerLink} to="/legal/terms-of-service">Terms &amp; Conditions</Link>.
            </p>
          </div>
          <div css={styles.disclaimerBlock}>
            <h3 css={styles.disclaimerTitle}>Farm Bill Compliance Disclaimer</h3>
            <p css={styles.disclaimerBody}>
              All products contain less than 0.3% hemp derived Delta 9 THC in compliance with the 2018 Farm Bill − This product is not available for shipment to the following states: Arkansas, Hawaii, Idaho, Kansas, Louisiana, Oklahoma, Oregon, Rhode Island, Utah, Vermont.
            </p>
          </div>
          <div css={styles.disclaimerBlock}>
            <h3 css={styles.disclaimerTitle}>Kratom Compliance Disclaimer</h3>
            <p css={styles.disclaimerBody}>
              You MUST be 21 years or older to purchase Kratom and kava extracts. We do not ship to the following states, cities, and counties in the US where Kratom is banned: Alabama, Connecticut, Ohio, Louisiana, Arkansas, Indiana, Rhode Island, Vermont, Wisconsin, Sarasota County FL, Alcorn, Calhoun, Itawamba, Lowndes, Monroe, Noxubee, Prentiss, Tippah, Tishomingo, and Union County, MS, Corinth, Derma, Fulton, Guntown, Iuka, Mantachie, Marietta, New Albany, Okolona, Oxford, Pontotoc, Ripley, Saltillo, Senatobia and Vardaman, MS, Monument and Parker, CO, San Diego, CA, Jerseyville, IL, and Oceanside, CA.
            </p>
          </div>
        </div>

        {/* Support contact strip — surface-level, discreet */}
        <div css={styles.supportInfo}>
          <a href="mailto:support@getfullproof.com" css={styles.supportLink}>
            support@getfullproof.com
          </a>
          <span css={styles.supportSeparator} aria-hidden="true">·</span>
          <a href="tel:+18777638930" css={styles.supportLink}>
            (877) 763-8930
          </a>
          <span css={styles.supportSeparator} aria-hidden="true">·</span>
          <address css={styles.supportAddress}>
            2006 N Sepulveda Blvd, #1019, Los Angeles, CA 90266
          </address>
        </div>

        {/* Footer Bottom */}
        <div css={styles.footerBottom}>
          <div css={styles.social}>
            <h2 css={styles.socialTitle}>
              Stay in touch
            </h2>
            <div css={{
              display: "flex", gap: "10px", justifyContent: "center", [mq[1]]: { gap: "20px" }
            }}>
              <a aria-label="Follow Fullproof on Facebook" href="https://www.facebook.com/getfullproof" target="_blank" rel="noreferrer"><FacebookIcon css={styles.socialIcons} /></a>
              <a aria-label="Follow Fullproof on Instagram" href="https://www.instagram.com/getfullproof" target="_blank" rel="noreferrer"><InstagramIcon css={styles.socialIcons} /></a>
            </div>
          </div>
          <div>
            <img src="/images/fullproof_logo_white.svg" alt="Fullproof" css={styles.logo} />
            <p css={styles.copyright}>© 2025 Fullproof Inc. All Rights Reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const styles = {
  footerContainer: {
    backgroundColor: "#000",
    color: "white",
    textAlign: "center",
    padding: "20px",
  },
  newsletterContainer: {
    textAlign: "center",
  },
  newsletterTitle: {
    fontSize: "1.4rem",
    fontWeight: "bold",
    padding: "20px 0 35px 0",
    display: "flex",
    maxWidth: '226px',
    textAlign: "left",
    [mq[1]]: {
      textAlign: "center",
      maxWidth: '100%',
      justifyContent: 'center'
    },
  },
  inputContainer: {
    display: "flex",
    width: "100%",
    maxWidth: "550px",
    margin: "0 auto",
    borderRadius: "10px",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    marginBottom: 10,
  },
  input: {
    display: "flex",
    width: "100%",
    border: "none",
    outline: "none",
    fontSize: ".9rem",
    padding: "10px 15px",
  },
  inputButton: {
    backgroundColor: "#fff",
    border: "none",
    paddingRight: 10,
    fontSize: "1rem",
    cursor: "pointer",
    color: "black",
    paddingTop: 3,
  },
  footerSection: {
    borderBottom: "1px solid #666",
    padding: "10px 0",
  },
  sectionTitle: {
    width: "100%",
    background: "none",
    border: "none",
    color: "white",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    padding: "3px 0",
    textAlign: "left",
    [mq[1]]: {
      fontSize: "1.15rem",
    },
  },
  social: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "30px",
    [mq[1]]: {
      marginBottom: "0",
      padding: "0 60px",
      borderRight: "1px solid #666"
    }
  },
  socialTitle: {
    fontSize: "1.25rem",
    [mq[1]]: {
      fontSize: "1.4rem",
    },
  },
  socialIcons: {
    color: "white",
    width: "26px",
    height: "26px",
    [mq[1]]: {
      width: "28px",
      height: "28px",
    },
  },
  linkContainer: {
    textAlign: "left",
    padding: "10px 0",
  },
  linkGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: "10px",
  },
  linkText: {
    fontSize: ".9rem",
    color: "white",
    textDecoration: "none",
    cursor: "pointer",
    width: "calc(33.333% - 10px)",
    textTransform: "capitalize",
    marginBottom: 15,
    [mq[1]]: {
      fontSize: "1rem",
    },
  },
  icon: {
    color: "white",
    width: "24px",
    height: "24px",
    [mq[1]]: {
      width: "26px",
      height: "26px",
    },
  },
  paymentMethods: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "20px",
    [mq[1]]: {
      flexDirection: "row",
      justifyContent: "center",
      gap: "16px",
    },
  },
  paymentMethodsLabel: {
    color: "#bbb",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  paymentMethodsList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  paymentMethodItem: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "52px",
    height: "33px",
    borderRadius: "4px",
    overflow: "hidden",
    boxSizing: "border-box",
    [mq[1]]: {
      width: "60px",
      height: "38px",
    },
  },
  paymentMethodItemCarded: {
    background: "#fff",
    padding: "4px 6px",
  },
  paymentMethodImage: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  coaLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    margin: "20px 0 0",
    width: "fit-content",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 600,
    textDecoration: "none",
    transition: "opacity 120ms ease",
    "&:hover": { opacity: 0.75 },
    [mq[1]]: {
      fontSize: "0.95rem",
    },
  },
  disclaimersContainer: {
    marginTop: "20px",
    paddingTop: "20px",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  disclaimerBlock: {},
  disclaimerTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "white",
    margin: "0 0 6px 0",
    [mq[1]]: {
      fontSize: "0.95rem",
    },
  },
  disclaimerBody: {
    fontSize: "0.7rem",
    color: "#bbb",
    lineHeight: 1.5,
    margin: 0,
    [mq[1]]: {
      fontSize: "0.75rem",
    },
  },
  disclaimerLink: {
    color: "#fff",
    textDecoration: "underline",
  },
  supportInfo: {
    marginTop: "22px",
    paddingTop: "18px",
    borderTop: "1px solid #333",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "6px",
    color: "#aaa",
    fontSize: "0.72rem",
    [mq[1]]: {
      flexDirection: "row",
      justifyContent: "center",
      gap: "14px",
      fontSize: "0.75rem",
    },
  },
  supportLink: {
    color: "#ddd",
    textDecoration: "none",
    "&:hover": { color: "#fff" },
  },
  supportSeparator: {
    color: "#555",
    display: "none",
    [mq[1]]: { display: "inline" },
  },
  supportAddress: {
    fontStyle: "normal",
    margin: 0,
  },
  footerBottom: {
    marginTop: "20px",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    [mq[1]]: {
      flexDirection: "row",
      justifyContent: "center",
      gap: "60px",
    },
  },
  logo: {
    height: "30px",
    marginBottom: "10px",
    [mq[1]]: {
      marginBottom: "15px",
    },
  },
  copyright: {
    color: "white",
    fontSize: "0.7rem",
  },
};

export default Footer;
